import { registerRoute } from '../router';
import { apiFetch, ApiError } from '../api';
import { getUser, type Role } from '../state';
import { escapeHtml } from '../format';
import { openModal } from '../components/modal';

interface UserSummary {
  id: string;
  email: string;
  name: string;
  role: Role;
  status: 'active' | 'disabled';
  createdAt: string;
}

const ROLES: Role[] = ['administrator', 'manager', 'operator', 'viewer'];

let users: UserSummary[] = [];

async function loadUsers(container: HTMLElement): Promise<void> {
  users = await apiFetch<UserSummary[]>('/api/users');
  renderList(container);
}

function renderList(container: HTMLElement): void {
  const listEl = container.querySelector('#users-list')!;
  const currentUserId = getUser()?.id;

  listEl.innerHTML = users
    .map(
      (user) => `
    <div class="card" style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">
      <div>
        <div style="font-weight:600;">${escapeHtml(user.name)} ${user.id === currentUserId ? '<span class="badge badge--muted">you</span>' : ''}</div>
        <div style="color:var(--clr-text-muted);font-size:0.85rem;margin-top:4px;">${escapeHtml(user.email)}</div>
        <div style="display:flex;gap:8px;margin-top:8px;">
          <span class="badge badge--orange">${escapeHtml(user.role)}</span>
          <span class="badge ${user.status === 'active' ? 'badge--success' : 'badge--muted'}">${user.status}</span>
        </div>
      </div>
      <div style="display:flex;gap:8px;flex-shrink:0;">
        <button class="btn btn--ghost" data-action="edit" data-id="${user.id}">Edit</button>
        <button class="btn btn--ghost" data-action="reset-password" data-id="${user.id}">Reset password</button>
      </div>
    </div>`,
    )
    .join('');

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="edit"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const user = users.find((u) => u.id === btn.dataset.id);
      if (user) openEditModal(container, user);
    });
  });

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="reset-password"]').forEach((btn) => {
    btn.addEventListener('click', () => openResetPasswordModal(btn.dataset.id!));
  });
}

function openCreateModal(container: HTMLElement): void {
  const roleOptions = ROLES.map((r) => `<option value="${r}">${r}</option>`).join('');

  const { root, close } = openModal(
    'New user',
    `
    <form id="user-form">
      <div class="field">
        <label for="user-email">Email</label>
        <input class="input" id="user-email" type="email" required>
      </div>
      <div class="field">
        <label for="user-name">Name</label>
        <input class="input" id="user-name" required>
      </div>
      <div class="field">
        <label for="user-role">Role</label>
        <select class="input" id="user-role">${roleOptions}</select>
      </div>
      <div class="field">
        <label for="user-password">Initial password</label>
        <input class="input" id="user-password" type="password" required minlength="8">
      </div>
      <p class="error-text" id="user-error"></p>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn--secondary" id="user-cancel">Cancel</button>
        <button type="submit" class="btn btn--primary">Create</button>
      </div>
    </form>
  `,
  );

  root.querySelector('#user-cancel')?.addEventListener('click', close);

  root.querySelector('#user-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const errorEl = root.querySelector('#user-error')!;
    errorEl.textContent = '';

    const payload = {
      email: (root.querySelector('#user-email') as HTMLInputElement).value.trim(),
      name: (root.querySelector('#user-name') as HTMLInputElement).value.trim(),
      role: (root.querySelector('#user-role') as HTMLSelectElement).value as Role,
      password: (root.querySelector('#user-password') as HTMLInputElement).value,
    };

    try {
      await apiFetch('/api/users', { method: 'POST', body: JSON.stringify(payload) });
      close();
      await loadUsers(container);
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : 'Something went wrong.';
    }
  });
}

function openEditModal(container: HTMLElement, user: UserSummary): void {
  const roleOptions = ROLES.map((r) => `<option value="${r}" ${user.role === r ? 'selected' : ''}>${r}</option>`).join('');

  const { root, close } = openModal(
    'Edit user',
    `
    <form id="user-edit-form">
      <div class="field">
        <label for="edit-name">Name</label>
        <input class="input" id="edit-name" required value="${escapeHtml(user.name)}">
      </div>
      <div class="field">
        <label for="edit-role">Role</label>
        <select class="input" id="edit-role">${roleOptions}</select>
      </div>
      <div class="field">
        <label for="edit-status">Status</label>
        <select class="input" id="edit-status">
          <option value="active" ${user.status === 'active' ? 'selected' : ''}>active</option>
          <option value="disabled" ${user.status === 'disabled' ? 'selected' : ''}>disabled</option>
        </select>
      </div>
      <p class="error-text" id="user-edit-error"></p>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn--secondary" id="user-edit-cancel">Cancel</button>
        <button type="submit" class="btn btn--primary">Save changes</button>
      </div>
    </form>
  `,
  );

  root.querySelector('#user-edit-cancel')?.addEventListener('click', close);

  root.querySelector('#user-edit-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const errorEl = root.querySelector('#user-edit-error')!;
    errorEl.textContent = '';

    const payload = {
      name: (root.querySelector('#edit-name') as HTMLInputElement).value.trim(),
      role: (root.querySelector('#edit-role') as HTMLSelectElement).value as Role,
      status: (root.querySelector('#edit-status') as HTMLSelectElement).value as 'active' | 'disabled',
    };

    try {
      await apiFetch(`/api/users/${user.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      close();
      await loadUsers(container);
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : 'Something went wrong.';
    }
  });
}

function openResetPasswordModal(userId: string): void {
  const { root, close } = openModal(
    'Reset password',
    `
    <form id="reset-form">
      <div class="field">
        <label for="reset-password">New password</label>
        <input class="input" id="reset-password" type="password" required minlength="8">
      </div>
      <p style="font-size:0.8rem;color:var(--clr-text-muted);margin-bottom:14px;">This signs the user out everywhere immediately.</p>
      <p class="error-text" id="reset-error"></p>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn--secondary" id="reset-cancel">Cancel</button>
        <button type="submit" class="btn btn--primary">Reset</button>
      </div>
    </form>
  `,
  );

  root.querySelector('#reset-cancel')?.addEventListener('click', close);

  root.querySelector('#reset-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const errorEl = root.querySelector('#reset-error')!;
    errorEl.textContent = '';
    const password = (root.querySelector('#reset-password') as HTMLInputElement).value;

    try {
      await apiFetch(`/api/users/${userId}/reset-password`, { method: 'POST', body: JSON.stringify({ password }) });
      close();
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : 'Something went wrong.';
    }
  });
}

registerRoute({
  path: '/users',
  title: 'Users',
  render: ({ container }) => {
    container.innerHTML = `
      <div class="page-header" style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;">
        <div>
          <h1>Users</h1>
          <p>Provision accounts and manage roles — there's no public signup by design.</p>
        </div>
        <button class="btn btn--primary" id="new-user-btn">+ New user</button>
      </div>
      <div id="users-list">Loading…</div>
    `;

    container.querySelector('#new-user-btn')?.addEventListener('click', () => openCreateModal(container));

    loadUsers(container).catch(() => {
      container.querySelector('#users-list')!.textContent = 'Could not load users.';
    });
  },
});
